import { motion, AnimatePresence } from 'framer-motion';
import { Building2, AlertCircle, Clock, ArrowLeft, FileCheck, Shield } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const AccountUnderReviewModal = ({ isOpen, onGoToDashboard }) => {
  const navigate = useNavigate();

  const handleGoToDashboard = () => {
    if (onGoToDashboard) {
      onGoToDashboard();
    } else {
      navigate('/dashboard');
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop - Non-dismissible */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[9999] bg-black bg-opacity-75 backdrop-blur-sm"
            style={{ pointerEvents: 'auto' }}
          />

          {/* Modal */}
          <div className="fixed inset-0 z-[10000] flex items-center justify-center p-3 sm:p-4 pointer-events-none">
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ 
                type: 'spring', 
                stiffness: 300, 
                damping: 30,
                duration: 0.4 
              }}
              className="relative bg-white rounded-xl sm:rounded-2xl shadow-2xl max-w-xs sm:max-w-sm w-full pointer-events-auto overflow-hidden max-h-[85vh] flex flex-col"
            >
              {/* Decorative gradient background */}
              <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 opacity-50" />
              
              {/* Animated border */}
              <div className="absolute inset-0 rounded-2xl border-4 border-blue-200 animate-pulse" />

              {/* Content - Scrollable */}
              <div className="relative p-5 sm:p-6 flex-1 overflow-y-auto">
                {/* Icon container with animation */}
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ 
                    type: 'spring', 
                    stiffness: 200, 
                    damping: 15,
                    delay: 0.2 
                  }}
                  className="flex justify-center mb-3"
                >
                  <div className="relative">
                    {/* Outer glow ring */}
                    <motion.div
                      animate={{
                        scale: [1, 1.1, 1],
                        opacity: [0.3, 0.5, 0.3],
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: 'easeInOut',
                      }}
                      className="absolute inset-0 bg-blue-400 rounded-full blur-xl"
                    />
                    
                    {/* Icon container */}
                    <div className="relative w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center shadow-2xl">
                      <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-full animate-ping opacity-20" />
                      <Building2 className="w-8 h-8 text-white relative z-10" strokeWidth={2.5} />
                    </div>
                  </div>
                </motion.div>

                {/* Title */}
                <motion.h2
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="text-lg sm:text-xl font-bold text-gray-900 text-center mb-1.5"
                >
                  Provider Account Under Review
                </motion.h2>

                {/* Subtitle */}
                <motion.p
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="text-center text-gray-600 mb-4 text-xs sm:text-sm"
                >
                  Your provider account is being reviewed by our team
                </motion.p>

                {/* Information box - Ultra Compact */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-3.5 mb-4"
                >
                  <div className="space-y-2.5">
                    {/* Status indicator */}
                    <div className="flex items-start space-x-2">
                      <Clock className="w-3.5 h-3.5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <p className="text-xs sm:text-sm text-gray-700 leading-snug">
                        Review in progress. Typically takes <strong>24-48 hours</strong>.
                      </p>
                    </div>

                    {/* Restrictions - Ultra Compact */}
                    <div className="flex items-start space-x-2 pt-2 border-t border-blue-200">
                      <AlertCircle className="w-3.5 h-3.5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <p className="text-xs sm:text-sm text-gray-700 leading-snug">
                        <strong>Restricted:</strong> Cannot create policies, edit policies, manage purchases, or process claims.
                      </p>
                    </div>

                    {/* What you can do - Ultra Compact */}
                    <div className="flex items-start space-x-2 pt-2 border-t border-blue-200">
                      <FileCheck className="w-3.5 h-3.5 text-emerald-600 mt-0.5 flex-shrink-0" />
                      <p className="text-xs sm:text-sm text-gray-700 leading-snug">
                        You can still access your dashboard to view account information and analytics.
                      </p>
                    </div>
                  </div>
                </motion.div>

                {/* Action button */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                  className="flex justify-center mb-3"
                >
                  <button
                    onClick={handleGoToDashboard}
                    className="group relative w-full px-5 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center justify-center space-x-2 overflow-hidden text-sm sm:text-base"
                  >
                    {/* Button glow effect */}
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-indigo-600 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                    
                    <ArrowLeft className="w-4 h-4 relative z-10 group-hover:-translate-x-1 transition-transform duration-200" />
                    <span className="relative z-10">Go to Dashboard</span>
                  </button>
                </motion.div>

                {/* Footer note - Ultra Compact */}
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.7 }}
                  className="text-center text-xs text-gray-500"
                >
                  Need help?{' '}
                  <a href="mailto:support@tripsecuregh.com" className="text-blue-600 hover:underline font-medium">
                    Contact Support
                  </a>
                </motion.p>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default AccountUnderReviewModal;
